import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { categoryService } from '../../../services/categoryService';
import { accountService } from '../../../services/accountService';

const ExtractedDataForm = ({ extractedData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    parentCategoryId: '',
    subcategoryId: '',
    accountId: '',
    notes: ''
  });

  const [parentCategories, setParentCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);

  useEffect(() => {
    loadParentCategories();
    loadAccounts();
  }, []);

  useEffect(() => {
    if (extractedData) {
      setFormData({
        date: extractedData?.date || new Date()?.toISOString()?.split('T')?.[0],
        description: extractedData?.merchantName || '',
        amount: extractedData?.totalAmount?.toString() || '',
        parentCategoryId: '',
        subcategoryId: extractedData?.categoryId || '',
        accountId: '',
        notes: extractedData?.lineItems?.length > 0 
          ? `Line items: ${extractedData?.lineItems?.map(item => `${item?.description} ($${item?.amount})`)?.join(', ')}`
          : ''
      });
    }
  }, [extractedData]);

  // Load subcategories when parent category changes
  useEffect(() => {
    if (formData?.parentCategoryId) {
      loadSubcategories(formData?.parentCategoryId);
    } else {
      setSubcategories([]);
      setFormData(prev => ({ ...prev, subcategoryId: '' }));
    }
  }, [formData?.parentCategoryId]);

  const loadParentCategories = async () => {
    try {
      setLoadingError(null);
      const data = await categoryService?.getParentCategories('expense');
      console.log('Loaded parent categories:', data);
      setParentCategories(data || []);
    } catch (error) {
      console.error('Error loading parent categories:', error);
      setLoadingError('Failed to load categories. Please refresh the page.');
    }
  };

  const loadSubcategories = async (parentId) => {
    try {
      setLoadingSubcategories(true);
      const data = await categoryService?.getSubcategories(parentId);
      console.log('Loaded subcategories for parent', parentId, ':', data);
      setSubcategories(data || []);
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const loadAccounts = async () => {
    try {
      setLoadingError(null);
      const data = await accountService?.getAll();
      console.log('Loaded accounts:', data);
      setAccounts(data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setLoadingError('Failed to load accounts. Please refresh the page.');
    }
  };

  const parentCategoryOptions = parentCategories?.map(cat => ({
    value: cat?.id,
    label: cat?.name
  })) || [];

  const subcategoryOptions = subcategories?.map(cat => ({
    value: cat?.id,
    label: cat?.name
  })) || [];

  const accountOptions = accounts?.map(acc => ({
    value: acc?.id,
    label: `${acc?.name} (${acc?.type})`
  })) || [];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    try {
      const amount = parseFloat(formData?.amount);
      
      // Use subcategory if selected, otherwise use parent category
      const categoryId = formData?.subcategoryId || formData?.parentCategoryId;
      
      if (!categoryId) {
        throw new Error('Please select a category');
      }

      await onSubmit({
        date: new Date(formData?.date)?.toISOString(),
        description: formData?.description,
        amount: -Math.abs(amount), // Negative for expense
        type: 'expense',
        categoryId: categoryId,
        accountId: formData?.accountId,
        notes: formData?.notes
      });
    } catch (error) {
      console.error('Error creating transaction:', error);
      alert(error?.message || 'Failed to create transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.8) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
          <Icon name="CheckCircle" className="w-3 h-3" />
          High ({Math.round(confidence * 100)}%)
        </span>
      );
    } else if (confidence >= 0.5) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
          <Icon name="AlertCircle" className="w-3 h-3" />
          Medium ({Math.round(confidence * 100)}%)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">
          <Icon name="XCircle" className="w-3 h-3" />
          Low ({Math.round(confidence * 100)}%)
        </span>
      );
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-medium p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Extracted Transaction Details</h2>
          <p className="text-sm text-muted-foreground mt-1">Review and edit before creating transaction</p>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="ScanLine" className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium text-primary">OCR Extracted</span>
        </div>
      </div>

      {/* Loading Error */}
      {loadingError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{loadingError}</p>
          </div>
        </div>
      )}

      {/* Quality Assessment */}
      {extractedData?.qualityAssessment && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 mb-1">Quality Assessment</p>
              <p className="text-xs text-blue-800">{extractedData?.qualityAssessment}</p>
            </div>
            {getConfidenceBadge(extractedData?.overallConfidence)}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Merchant Name */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Merchant Name
            </label>
            {getConfidenceBadge(extractedData?.merchantConfidence)}
          </div>
          <Input
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
            placeholder="Enter merchant name"
            required
          />
        </div>

        {/* Date */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Transaction Date
            </label>
            {getConfidenceBadge(extractedData?.dateConfidence)}
          </div>
          <Input
            type="date"
            value={formData?.date}
            onChange={(e) => handleChange('date', e?.target?.value)}
            required
          />
        </div>

        {/* Amount */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Total Amount
            </label>
            {getConfidenceBadge(extractedData?.amountConfidence)}
          </div>
          <Input
            type="number"
            step="0.01"
            value={formData?.amount}
            onChange={(e) => handleChange('amount', e?.target?.value)}
            placeholder="0.00"
            required
          />
          {extractedData?.taxAmount > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              Tax amount detected: ${extractedData?.taxAmount?.toFixed(2)} (Confidence: {Math.round(extractedData?.taxConfidence * 100)}%)
            </p>
          )}
        </div>

        {/* Parent Category */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">
              Category *
            </label>
            {extractedData?.categoryConfidence > 0 && getConfidenceBadge(extractedData?.categoryConfidence)}
          </div>
          <Select
            value={formData?.parentCategoryId}
            onChange={(value) => handleChange('parentCategoryId', value)}
            options={parentCategoryOptions}
            placeholder="Select a category"
            required
          />
          {extractedData?.categoryName && (
            <p className="text-xs text-muted-foreground mt-1">
              AI suggested: {extractedData?.categoryName}
            </p>
          )}
          {parentCategories?.length === 0 && !loadingError && (
            <p className="text-xs text-amber-600 mt-1">
              No categories found. Please create categories in Settings.
            </p>
          )}
        </div>

        {/* Subcategory (only show if parent selected) */}
        {formData?.parentCategoryId && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Subcategory (Optional)
            </label>
            {loadingSubcategories ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                Loading subcategories...
              </div>
            ) : subcategories?.length > 0 ? (
              <Select
                value={formData?.subcategoryId}
                onChange={(value) => handleChange('subcategoryId', value)}
                options={subcategoryOptions}
                placeholder="Select a subcategory"
              />
            ) : (
              <p className="text-sm text-muted-foreground italic">No subcategories available for this category</p>
            )}
          </div>
        )}

        {/* Account */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Account *
          </label>
          <Select
            value={formData?.accountId}
            onChange={(value) => handleChange('accountId', value)}
            options={accountOptions}
            placeholder="Select an account"
            required
          />
          {accounts?.length === 0 && !loadingError && (
            <p className="text-xs text-amber-600 mt-1">
              No accounts found. Please create an account first.
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={formData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
            placeholder="Add any additional notes..."
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-background text-foreground"
          />
        </div>

        {/* Line Items Display */}
        {extractedData?.lineItems?.length > 0 && (
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium text-foreground mb-2">Detected Line Items:</p>
            <div className="space-y-1">
              {extractedData?.lineItems?.map((item, index) => (
                <div key={index} className="flex justify-between text-xs text-muted-foreground">
                  <span>{item?.description}</span>
                  <span className="font-medium">${item?.amount?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            disabled={isSubmitting}
          >
            <Icon name="X" className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Icon name="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Icon name="Check" className="w-4 h-4 mr-2" />
                Create Transaction
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ExtractedDataForm;